// House defaults (docs/tune-library-plan.md §3): the owner sets a tune up and
// saves it as the default for everyone. This is the reach to Supabase's
// `tune_defaults` table (migration 0007) and the sync that keeps the device's
// cache (`houseDefaults` in the store) current.
//
// Everyone reads (signed in or not — a student arriving from a link hears the
// tune as it was set up); only an admin writes, through the
// `save_tune_default` function, which bumps the revision the client compares
// to know a default has moved. The cache is what opening a tune reads, so a
// tune opens instantly and offline; a fetch only refreshes it.

import { bootAuth, getSupabaseRaw } from '@/auth/session';
import { getStylePreset } from '@/music/style';
import { useStore } from '@/state/store';
import { normalizeHouseDefault, type HouseDefault, type HouseSettings } from '@/state/tunePlayback';

const TABLE = 'tune_defaults';

/** A fetched row: the cached default plus who saved it (the admin list). */
export interface TuneDefaultRow extends HouseDefault {
  updatedBy: string | null;
}

type Result<T> = { data: T | null; error: { message: string } | null };

/** The slice of supabase-js this module uses (tests fake it). */
export interface TuneDefaultsClient {
  from(table: string): {
    select(columns: string): PromiseLike<Result<Record<string, unknown>[]>>;
    delete(): {
      eq(column: string, value: string): PromiseLike<{ error: { message: string } | null }>;
    };
  };
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<Result<unknown>>;
}

let testClient: TuneDefaultsClient | null = null;

function client(): TuneDefaultsClient | null {
  return testClient ?? (getSupabaseRaw() as TuneDefaultsClient | null);
}

/** The band a stored feel falls back on for anything it leaves out. */
const baseBand = () => getStylePreset('swing')!.band;

function rowOf(raw: Record<string, unknown>): TuneDefaultRow | null {
  const house = normalizeHouseDefault(
    {
      tuneKey: raw.tune_key,
      title: raw.title,
      composer: raw.composer,
      settings: raw.doc,
      revision: raw.revision,
      updatedAt: raw.updated_at,
    },
    baseBand(),
  );
  if (!house) return null;
  return { ...house, updatedBy: typeof raw.updated_by === 'string' ? raw.updated_by : null };
}

/**
 * Every house default, newest first. Null when this build has no backend (the
 * cache then stays as it is); a transport failure throws.
 */
export async function fetchTuneDefaults(): Promise<TuneDefaultRow[] | null> {
  const c = client();
  if (!c) return null;
  const { data, error } = await c
    .from(TABLE)
    .select('tune_key, title, composer, doc, revision, updated_by, updated_at');
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map(rowOf).filter((row): row is TuneDefaultRow => row !== null);
  rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return rows;
}

/** Save (or replace) a tune's default. Admins only — the server refuses anyone else. */
export async function saveTuneDefault(input: {
  tuneKey: string;
  title: string;
  composer: string;
  settings: HouseSettings & { schemaVersion: number };
}): Promise<HouseDefault> {
  const c = client();
  if (!c) throw new Error('Sign in to save defaults.');
  const { data, error } = await c.rpc('save_tune_default', {
    p_tune_key: input.tuneKey,
    p_title: input.title,
    p_composer: input.composer,
    p_doc: input.settings,
  });
  if (error) throw new Error(error.message);
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined;
  const revision = typeof row?.saved_revision === 'number' ? row.saved_revision : null;
  if (!revision) throw new Error('The default was not saved.');
  return {
    tuneKey: input.tuneKey,
    title: input.title,
    composer: input.composer,
    settings: input.settings,
    revision,
    updatedAt: typeof row?.saved_at === 'string' ? row.saved_at : new Date().toISOString(),
  };
}

/** Remove a tune's default: it opens as the tune itself again for everyone. */
export async function removeTuneDefault(tuneKey: string): Promise<void> {
  const c = client();
  if (!c) throw new Error('Sign in first.');
  const { error } = await c.from(TABLE).delete().eq('tune_key', tuneKey);
  if (error) throw new Error(error.message);
}

/** Refresh the device's cache. Quietly does nothing without a backend or a
 *  connection: the cache it already has is the right thing to play from. */
export async function syncTuneDefaults(): Promise<void> {
  try {
    if (!testClient) await bootAuth();
    const rows = await fetchTuneDefaults();
    if (rows) {
      useStore.getState().setHouseDefaults(rows.map(({ updatedBy: _by, ...house }) => house));
    }
  } catch {
    // Offline or refused: keep the cache.
  }
}

/** How long a returning tab waits before asking again. */
const RESYNC_MS = 5 * 60 * 1000;

/**
 * Sync now, and again when the app comes back into view after a while, so a
 * default the owner saves during a class reaches an open tab. Returns the
 * listener's cleanup.
 */
export function startTuneDefaultsSync(now: () => number = Date.now): () => void {
  let last = now();
  void syncTuneDefaults();
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || now() - last < RESYNC_MS) return;
    last = now();
    void syncTuneDefaults();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}

export function __setTuneDefaultsTestClient(c: TuneDefaultsClient | null): void {
  testClient = c;
}
